/*
Reusable error state for page-level fetches. Renders a card with a
warning glyph, a short title (defaulted via i18n when not supplied),
the underlying message, and an optional Retry button.

The retry handler is the page's own load function — when present,
the user can recover without reloading the whole SPA. Without it,
the component just shows the failure (e.g. for a GET that has no
sensible retry, or where the page already navigated past the error).
*/

import type { JSX } from "react";
import { useTranslation } from "react-i18next";

export interface ErrorStateProps
{
	title?:		string;		// Optional override; defaults to common.error_loading_short
	message:	string;		// The underlying error message (shown verbatim)
	onRetry?:	() => void;	// When set, renders a Retry button
}

export function ErrorState(props: ErrorStateProps): JSX.Element
{
	const { t } = useTranslation("common");

	return (
		<div className="card error-state" role="alert">
			<div className="error-state__icon" aria-hidden="true">⚠</div>
			<div className="error-state__body">
				<h2 style={{ margin: 0 }}>{props.title ?? t("error_state_title")}</h2>
				<p className="muted" style={{ margin: "0.4rem 0 0 0" }}>{props.message}</p>
				{props.onRetry !== undefined
					? (
						<div style={{ marginTop: "0.75rem" }}>
							<button type="button" className="button" onClick={props.onRetry}>
								{t("retry")}
							</button>
						</div>
					)
					: null}
			</div>
		</div>
	);
}
